import type { CcPreviewCampaign } from '@elva/ui';
import { growthGet } from '@/data/http';
import type { BeCampaignDraft, BeCampaignDraftResponse } from '@/data/marketingApiTypes';
import { deepCamel, num, str } from '@/data/adapt';
import { getErrorStatus } from '@/data/errors';

function mapKeywordRows(rows: BeCampaignDraft['keywords'] | undefined) {
  return (rows ?? []).map((row) => {
    const c = deepCamel<Record<string, unknown>>(row);
    return {
      keyword: str(c.keyword),
      volume: num(c.volume),
      cpc: num(c.cpc),
      competition: (str(c.competition, 'Low') as 'Very Low' | 'Low') ?? 'Low',
    };
  });
}

function mapAdRows(rows: BeCampaignDraft['ads'] | undefined) {
  return (rows ?? []).map((row) => {
    const c = deepCamel<Record<string, unknown>>(row);
    return {
      label: str(c.label),
      headline: str(c.headline),
      url: str(c.url),
      description: str(c.description),
      extensions: (c.extensions as string[] | undefined) ?? [],
    };
  });
}

function mapLandingRows(rows: BeCampaignDraft['landing'] | undefined) {
  return (rows ?? []).map((row) => {
    const c = deepCamel<Record<string, unknown>>(row);
    return {
      icon: str(c.icon, '✓'),
      label: str(c.label),
      description: str(c.description),
    };
  });
}

function mapTrackingRows(rows: BeCampaignDraft['tracking'] | undefined) {
  return (rows ?? []).map((row) => {
    const c = deepCamel<Record<string, unknown>>(row);
    return {
      label: str(c.label),
      description: str(c.description),
    };
  });
}

function draftBudgetDollars(be: BeCampaignDraft): number {
  // Live draft emits `budget` in cents (e.g. 90000), same as projected_monthly_budget_cents.
  if (be.budget_cents != null) return Math.round(be.budget_cents / 100);
  if (be.budget != null) {
    const n = num(be.budget);
    return n >= 1000 ? Math.round(n / 100) : n;
  }
  return 0;
}

export function adaptCampaignDraft(be: BeCampaignDraft): CcPreviewCampaign {
  return {
    service: str(be.service),
    icon: str(be.icon, '🦷'),
    budget: draftBudgetDollars(be),
    radiusMiles: num(be.radiusMiles ?? be.radius_miles, 7),
    confidence: num(be.confidence),
    instructions: str(be.instructions, '—'),
    target: str(be.target, '—'),
    whyNow: str(be.whyNow ?? be.why_now, '—'),
    patients: str(be.patients, '—'),
    costPerPatient: str(be.costPerPatient ?? be.cost_per_patient, '—'),
    avgCaseValue: num(be.avg_case_value),
    roi: str(be.roi, '—'),
    roiEstimateMin: num(be.roiEstimateMin ?? be.roi_estimate_min),
    roiEstimateMax: num(be.roiEstimateMax ?? be.roi_estimate_max),
    lifetimeNote: str(be.lifetime_note, '—'),
    landingUrl: str(be.landingUrl ?? be.landing_url, '—'),
    keywords: mapKeywordRows(be.keywords),
    ads: mapAdRows(be.ads),
    landing: mapLandingRows(be.landing),
    tracking: mapTrackingRows(be.tracking),
  };
}

export async function fetchCampaignDraft(suggestionId: string): Promise<CcPreviewCampaign | null> {
  try {
    const wire = await growthGet<BeCampaignDraftResponse | BeCampaignDraft>(
      `/campaigns/draft/${suggestionId}/`,
    );
    const draft =
      wire && typeof wire === 'object' && 'draft' in wire
        ? (wire as BeCampaignDraftResponse).draft
        : (wire as BeCampaignDraft);
    if (!draft) return null;
    return adaptCampaignDraft(draft);
  } catch (error) {
    if (getErrorStatus(error) === 404) return null;
    throw error;
  }
}
